import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Connexion impossible. Vérifiez vos identifiants.";
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-jm-ink relative overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(70% 60% at 50% 100%, rgba(217,119,6,0.14), transparent 60%), linear-gradient(180deg,#0d0e12,#12141c)",
        }}
      />
      <div className="absolute inset-0 jm-grain opacity-40 pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md mx-4 border border-jm-brass/20 bg-jm-card/80 backdrop-blur p-8 sm:p-10"
      >
        <Link to="/" data-testid="admin-login-home-link" className="font-mono text-[10px] uppercase tracking-[0.28em] text-jm-mute hover:text-jm-brass transition-colors">
          ← joymessenger.fr
        </Link>
        <h1 className="mt-6 font-serif text-3xl text-jm-bone">Espace d'administration</h1>
        <p className="mt-2 text-sm text-jm-mute">Réservé à l'équipe Joy Messenger.</p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            data-testid="admin-email-input"
            className="w-full bg-transparent border border-jm-brass/25 focus:border-jm-amber px-4 py-3.5 text-sm text-jm-bone placeholder:text-jm-mute focus:outline-none transition-colors"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            data-testid="admin-password-input"
            className="w-full bg-transparent border border-jm-brass/25 focus:border-jm-amber px-4 py-3.5 text-sm text-jm-bone placeholder:text-jm-mute focus:outline-none transition-colors"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-jm-amber text-jm-ink py-3.5 text-sm font-medium tracking-wide hover:bg-jm-gold transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
